import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AnalyticsState, Option, TopProductExportData, TopDiscountCodesExportData } from "lib/types/state"

const initialState: AnalyticsState = {
  filteredDays: 1,
  startDate: "",
  endDate: "",
  storeGroups: [],
  storeMarkets: [],
  selectedCampaignGroups: [],
  selectedCampaigns: [],
  topProductsFilteredBy: "all",
  topProductsFilteredByType: "variant",
  topProductsSortBy: "quantity",
  topProductsFilteredByHits: 5,
  topCodesFilteredByHits: 5,
  productNames: {},
  discountCodeRuleNames: {},
  discountCodeRuleNamesLoaded: false,
  discountExternalRuleNames: {},
  discountExternalRuleNamesLoaded: false,
  filteredByExcludingTax: true,
  topDiscountFilteredByType: 'codes',
  topProductsExportData: null,
  topDiscountCodesExportData: null
}

function reset(state: AnalyticsState) {
  state.filteredDays = initialState.filteredDays
  state.storeGroups = initialState.storeGroups
  state.storeMarkets = initialState.storeMarkets
  state.selectedCampaignGroups = initialState.selectedCampaignGroups
  state.selectedCampaigns = initialState.selectedCampaigns
  state.topProductsFilteredBy = initialState.topProductsFilteredBy
  state.topProductsFilteredByType = initialState.topProductsFilteredByType
  state.topProductsSortBy = initialState.topProductsSortBy
  state.topProductsFilteredByHits = initialState.topProductsFilteredByHits
  state.topCodesFilteredByHits = initialState.topCodesFilteredByHits
  state.productNames = initialState.productNames
  state.discountCodeRuleNames = initialState.discountCodeRuleNames
  state.discountCodeRuleNamesLoaded = initialState.discountCodeRuleNamesLoaded
  state.discountExternalRuleNames = initialState.discountExternalRuleNames
  state.discountExternalRuleNamesLoaded = initialState.discountExternalRuleNamesLoaded
  state.filteredByExcludingTax = initialState.filteredByExcludingTax
  state.topDiscountFilteredByType = initialState.topDiscountFilteredByType
  state.topProductsExportData = initialState.topProductsExportData
  state.topDiscountCodesExportData = initialState.topDiscountCodesExportData
}

export const slice = createSlice({
  name: "analytics",
  initialState: { ...initialState },
  reducers: {
    reset: reset,
    setFilteredDays(state, action: PayloadAction<number>) {
      state.filteredDays = action.payload
    },
    setStartDate(state, action: PayloadAction<string>) {
      state.startDate = action.payload
    },
    setEndDate(state, action: PayloadAction<string>) {
      state.endDate = action.payload
    },
    setSelectedStoreGroups(state, action: PayloadAction<Option[]>) {
      state.storeGroups = action.payload
    },
    setSelectedStoreMarkets(state, action: PayloadAction<Option[]>) {
      state.storeMarkets = action.payload
    },
    setSelectedCampaignGroups(state, action: PayloadAction<Option[]>) {
      state.selectedCampaignGroups = action.payload
    },
    setSelectedCampaigns(state, action: PayloadAction<Option[]>) {
      state.selectedCampaigns = action.payload
    },
    setTopProductsFilteredBy(state, action: PayloadAction<string>) {
      state.topProductsFilteredBy = action.payload
    },
    setTopProductsFilteredByType(state, action: PayloadAction<string>) {
      state.topProductsFilteredByType = action.payload
    },
    setTopProductsSortBy(state, action: PayloadAction<'quantity' | 'totalSales'>) {
      state.topProductsSortBy = action.payload
    },
    setTopProductsFilteredByHits(state, action: PayloadAction<number>) {
      state.topProductsFilteredByHits = action.payload
    },
    setTopCodesFilteredByHits(state, action: PayloadAction<number>) {
      state.topCodesFilteredByHits = action.payload
    },
    setProductNames(state, action: PayloadAction<{ [productId: string]: string; }>) {
      state.productNames = action.payload
    },
    setFilteredByExcludingTax(state, action: PayloadAction<boolean>) {
      state.filteredByExcludingTax = action.payload
    },
    setTopDiscountFilteredByType(state, action: PayloadAction<'codes' | 'code rules' | 'external rules'>) {
      state.topDiscountFilteredByType = action.payload
    },
    setDiscountCodeRuleNames(state, action: PayloadAction<{ [ruleId: string]: string; }>) {
      state.discountCodeRuleNames = action.payload
      state.discountCodeRuleNamesLoaded = true
    },
    setDiscountExternalRuleNames(state, action: PayloadAction<{ [ruleId: string]: string; }>) {
      state.discountExternalRuleNames = action.payload
      state.discountExternalRuleNamesLoaded = true
    },
    setTopProductsExportData(state, action: PayloadAction<TopProductExportData>) {
      state.topProductsExportData = action.payload
    },
    setTopDiscountCodesExportData(state, action: PayloadAction<TopDiscountCodesExportData>) {
      state.topDiscountCodesExportData = action.payload
    }
  }
})

const { actions, reducer } = slice
export const {
  setFilteredDays,
  setStartDate,
  setEndDate,
  setSelectedStoreGroups,
  setSelectedStoreMarkets,
  setSelectedCampaignGroups,
  setSelectedCampaigns,
  setTopProductsFilteredBy,
  setTopProductsFilteredByType,
  setTopProductsSortBy,
  setTopProductsFilteredByHits,
  setTopCodesFilteredByHits,
  setProductNames,
  setFilteredByExcludingTax,
  setTopDiscountFilteredByType,
  setDiscountCodeRuleNames,
  setDiscountExternalRuleNames,
  setTopProductsExportData,
  setTopDiscountCodesExportData
} = actions

export default reducer
